import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Renter-side consent switch, one per match. This is the only screening
// surface renters ever see: they choose to share their verified profile,
// the lister orders and pays for the checks.
export default function ConsentToggle({ matchId, initialGranted = false }) {
  const [granted, setGranted] = useState(initialGranted);
  const [busy, setBusy] = useState(false);

  async function toggle() {
    const next = !granted;
    setBusy(true);
    try {
      await api.post("/renters/me/screening-consent", { match_id: matchId, granted: next });
      setGranted(next);
      toast.success(next ? "Your verified profile is shared with this lister." : "Sharing turned off.");
    } catch (error) {
      // Consent is a legal record: NEVER show it as granted unless the server
      // actually recorded it. On any failure (including offline), leave the
      // switch unchanged and ask the renter to retry — no local false-success.
      toast.warning("We could not update your consent right now. Please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-3 border-b border-brand-line bg-white px-4 py-2.5">
      <ShieldCheck size={16} className="shrink-0 text-brand-lime" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-brand-ink">Share my verified profile</p>
        <p className="text-xs leading-snug text-brand-secondary">
          Let this lister run reference and background checks on you. You stay in control.
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={granted}
        aria-label="Share my verified profile"
        onClick={toggle}
        disabled={busy}
        className={`relative h-6 w-11 shrink-0 rounded-full transition ${granted ? "bg-brand-primary" : "bg-neutral-200"} disabled:opacity-60`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-hairline transition-all ${granted ? "left-[22px]" : "left-0.5"}`}
        />
      </button>
    </div>
  );
}
